/**
 * BrokenLinkResender — Super-admin tool.
 *
 * Walks `dealer_emails_sent` for the last N days, finds any messages whose body
 * still references the preview/test URL (the env-var bug that shipped people to
 * the wrong dashboard), and sends each affected recipient a short fix-up email
 * with the correct production matches link.
 *
 * Flow:
 *   1. "Preview impact" → runs server-side dry-run; shows # of recipients
 *   2. "Send fix-up emails" → confirmation modal → actually fires the sends
 *
 * Audit trail is written to `broken_link_fixups_sent` on the server so the
 * same recipients never get re-blasted.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, AlertTriangle, Loader2, Send, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrokenLinkResender = ({ isDark }) => {
  const [sinceDays, setSinceDays] = useState(14);
  const [scanning, setScanning] = useState(false);
  const [sending, setSending] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const runScan = async () => {
    setScanning(true);
    setScanResult(null);
    setSendResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/resend-broken-links`,
        { since_days: Number(sinceDays) || 14, dry_run: true },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      setScanResult(res.data);
      toast.success(`${res.data.impacted_recipients} affected recipient(s) found`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const fireSends = async () => {
    setShowConfirm(false);
    setSending(true);
    setSendResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/resend-broken-links`,
        { since_days: Number(sinceDays) || 14, dry_run: false, limit: 500 },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      setSendResult(res.data);
      toast.success(`Sent ${res.data.sent} fix-up email(s)`);
      // Refresh the scan so the count drops
      runScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const card = isDark
    ? 'bg-white/[0.03] border border-white/10 backdrop-blur-sm'
    : 'bg-white border border-gray-200 shadow-sm';

  return (
    <div className={`rounded-2xl p-5 sm:p-6 ${card}`} data-testid="broken-link-resender">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-400/30 flex items-center justify-center shrink-0">
          <ShieldAlert className="w-5 h-5 text-amber-400" />
        </div>
        <div className="min-w-0">
          <h3 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Resend Broken Email Links
          </h3>
          <p className={`text-xs sm:text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            Finds anyone who recently received an email pointing at the preview/test URL and sends them the correct production link.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <Label htmlFor="since-days" className={isDark ? 'text-slate-300' : ''}>Look back (days)</Label>
          <Input
            id="since-days"
            type="number"
            min="1"
            max="90"
            value={sinceDays}
            onChange={(e) => setSinceDays(e.target.value)}
            className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
            data-testid="resender-since-days"
          />
        </div>
        <Button
          type="button"
          onClick={runScan}
          disabled={scanning || sending}
          variant="outline"
          className={`rounded-full ${isDark ? 'bg-white/5 border-white/15 text-white hover:bg-white/10' : ''}`}
          data-testid="resender-scan-btn"
        >
          {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          Preview impact
        </Button>
        <Button
          type="button"
          onClick={() => setShowConfirm(true)}
          disabled={!scanResult || scanResult.impacted_recipients === 0 || scanning || sending}
          className="rounded-full bg-[#F2A900] hover:bg-amber-400 text-black font-bold"
          data-testid="resender-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Send fix-up emails
        </Button>
      </div>

      {/* Scan result */}
      {scanResult && (
        <div
          className={`rounded-xl px-4 py-3 text-sm ${
            isDark ? 'bg-white/5 border border-white/10 text-slate-300' : 'bg-gray-50 border border-gray-200 text-gray-700'
          }`}
          data-testid="resender-scan-result"
        >
          <p className="font-semibold mb-1">
            {scanResult.impacted_recipients} recipient(s) impacted in the last {scanResult.since_days} day(s).
          </p>
          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
            Scanned {scanResult.scanned_emails} recent send(s).
          </p>
          {scanResult.sample?.length > 0 && (
            <ul className={`mt-3 text-xs space-y-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              {scanResult.sample.slice(0, 10).map((s) => (
                <li key={s.lead_email} className="truncate">
                  • <span className="font-mono">{s.lead_email}</span> — last broken: {new Date(s.last_broken_sent_at).toLocaleString()}
                </li>
              ))}
              {scanResult.sample.length > 10 && (
                <li className="italic">…and {scanResult.impacted_recipients - 10} more</li>
              )}
            </ul>
          )}
        </div>
      )}

      {/* Send result */}
      {sendResult && (
        <div
          className={`mt-3 rounded-xl px-4 py-3 text-sm ${
            isDark ? 'bg-emerald-500/10 border border-emerald-400/30 text-emerald-200' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
          }`}
          data-testid="resender-send-result"
        >
          ✅ Sent <strong>{sendResult.sent}</strong> fix-up email(s). {sendResult.failed_count > 0 && `${sendResult.failed_count} failed.`} Production URL used: <span className="font-mono">{sendResult.production_url_used}</span>
        </div>
      )}

      {/* Confirm modal */}
      {showConfirm && scanResult && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowConfirm(false)}
          data-testid="resender-confirm-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl shadow-2xl p-6 ${
              isDark ? 'bg-gray-900 border border-gray-700 text-white' : 'bg-white text-gray-900'
            }`}
            data-testid="resender-confirm-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-extrabold mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Send {scanResult.impacted_recipients} fix-up email(s)?
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Each affected recipient gets one short email apologizing for the broken link and pointing them to the correct production matches dashboard. This can&apos;t be undone.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
              <Button
                onClick={() => setShowConfirm(false)}
                variant="outline"
                className={`rounded-full ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700' : ''}`}
                data-testid="resender-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={fireSends}
                className="bg-[#F2A900] hover:bg-amber-400 text-black font-bold rounded-full"
                data-testid="resender-confirm-fire-btn"
              >
                <Send className="w-4 h-4 mr-2" /> Send now
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokenLinkResender;
